import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  Checkbox,
  Icon_Kebab,
  Menu,
  Modal,
  PageContainer,
  SpinnerCenter,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Navigate, useLocation, useParams } from "react-router-dom";

import teamPromoIllustration from "../assets/images/team-promo-illustration.webp";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import Heading from "../components/Heading/Heading";
import { AddToSpaceModal } from "../components/MemberSelectionFilterRow/AddToSpaceModal/AddToSpaceModal";
import MemberSelectionFilterRow from "../components/MemberSelectionFilterRow/MemberSelectionFilterRow";
import { RemoveFromSpaceModal } from "../components/MemberSelectionFilterRow/RemoveFromSpaceModal/RemoveFromSpaceModal";
import { TeamInviteModal } from "../components/TeamInviteModal/TeamInviteModal";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import { OverlayPlayer, OverlayPlayerHandle, Thumbnail } from "../components/VimeoVideo/VimeoVideo";
import { defaultTeamspaceName } from "../config";
import useNavigation from "../hooks/navigation";
import useAccount from "../hooks/useAccount";
import {
  NonSeatConsumingTeamsUserRoles,
  TeamsUserRoles,
  TeamsUserRolesDisplayNames,
} from "../hooks/useAccountTypes";
import { useGuardChinaRestrictedPages } from "../hooks/useGuardChinaRestrictedPages";
import {
  useDeleteTeamInvitation,
  useDeleteTeamMember,
  useTeam,
  useTeamMembers,
  useTeamSubscriptions,
} from "../hooks/useTeams";
import type { TeamMember } from "../hooks/useTeamsTypes";
import { useTracker } from "../hooks/useTracker";
import { ElementClickAction, ElementClickLocation, ModalShownAction } from "../lib/TrackingAdapter";
import { type URLState, URLStateActions } from "../types";

import style from "./TeamMembersPage.module.css";

const useFilteredTeamMembersData = (filter: string) => {
  const { teamID } = useParams();
  const teamMembersQueryResult = useTeamMembers(teamID);

  const filteredTeamMembersData = useMemo(
    () =>
      teamMembersQueryResult.data?.users.filter((teamMember) => {
        return (
          !filter ||
          teamMember.email.toLowerCase().includes(filter.toLowerCase()) ||
          teamMember.name?.toLowerCase().includes(filter.toLowerCase()) ||
          teamMember.spaces
            ?.map(({ name }) => name?.toLowerCase())
            .join("")
            .includes(filter.toLowerCase())
        );
      }),
    [filter, teamMembersQueryResult.data]
  );

  return { ...teamMembersQueryResult, data: filteredTeamMembersData };
};

const MembersVideo = () => {
  const tracker = useTracker();
  const navigation = useNavigation();

  const playerRef = useRef<OverlayPlayerHandle | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <>
      <Thumbnail
        src={teamPromoIllustration}
        alt="Team promo illustration"
        isLoading={!isLoaded}
        onClick={() => {
          setIsOpen(true);
          playerRef.current?.loadAndPlay("1144497089");
          tracker.trackButtonClick({
            action: ElementClickAction.OPEN_TEAMS_VIDEO,
            location: ElementClickLocation.HERO,
            details: "w9eaehhsum",
          });
        }}
      />
      <OverlayPlayer
        ref={playerRef}
        videoId="1144497089"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onReady={() => {
          setIsLoaded(true);
        }}
      >
        <Button
          onClick={() => {
            tracker.trackButtonClick({
              action: ElementClickAction.GO_TO_TEAMS_MARKETING,
              location: ElementClickLocation.VIDEO_CAPTION,
            });
            navigation.openTeamsMarketingPage();
          }}
          style={{ margin: "20px auto 2px", display: "block" }}
          variant="grayscale"
        >
          Learn more
        </Button>
      </OverlayPlayer>
    </>
  );
};

const Capsule: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className={style.capsule}>{children}</div>;
};

const TeamMembersPage = () => {
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isNotTeamOwnerModalOpen, setIsNotTeamOwnerModalOpen] = useState(false);
  const [isCancelledModalOpen, setIsCancelledModalOpen] = useState(false);
  const [isGracePeriodModalOpen, setIsGracePeriodModalOpen] = useState(false);
  const [selectedMemberToRemove, setSelectedMemberToRemove] = useState<TeamMember | null>(null);
  const [historyState, setHistoryState] = useState<URLState | null>(null);

  const tracker = useTracker();
  const chinaRestricted = useGuardChinaRestrictedPages();

  const { teamID } = useParams();
  const { teamData, isOwner, isSeatManager, isGuest } = useTeam(teamID);

  const [filter, setFilter] = useState("");
  const { data: teamMembersData, isLoading: isMembersLoading } = useFilteredTeamMembersData(filter);

  const { data: teamSubscriptionsData, isLoading: isSubscriptionsLoading } =
    useTeamSubscriptions(teamID);
  const { data: accountData } = useAccount();

  const hasFreeSeats =
    teamSubscriptionsData?.seatBasedTeamSubscription?.editorSeats &&
    teamSubscriptionsData?.seatBasedTeamSubscription.editorSeatsUsed &&
    teamSubscriptionsData?.seatBasedTeamSubscription?.editorSeats -
      teamSubscriptionsData?.seatBasedTeamSubscription?.editorSeatsUsed >
      0;

  const isLoading = isMembersLoading || isSubscriptionsLoading || !teamMembersData || !teamData;

  const { mutateAsync: deleteTeamMemberMutation } = useDeleteTeamMember(teamID);
  const { mutateAsync: removeInvitationMutation } = useDeleteTeamInvitation(teamID);

  const handleRemoval = useCallback(
    async ({ userID, invitationID }: { userID?: string; invitationID?: string }) => {
      if (invitationID) {
        await removeInvitationMutation({ invitationID });
        return;
      }

      if (userID) {
        tracker.trackButtonClick({
          action: ElementClickAction.CONFIRM,
          location: ElementClickLocation.REMOVE_MEMBER_MODAL,
        });
        await deleteTeamMemberMutation({ userID });
        setSelectedMemberToRemove(null);
        return;
      }
    },
    [deleteTeamMemberMutation, removeInvitationMutation, tracker]
  );

  const handleInviteAction = useCallback(() => {
    if (!isOwner && !isSeatManager) {
      setIsNotTeamOwnerModalOpen(true);
      tracker.trackModalShown(ModalShownAction.INVITE_UNAVAILABLE, "not_team_owner");
      return;
    }

    if (!hasFreeSeats && teamSubscriptionsData?.hasNonAutoRenewingSelfServeTeamSubscription) {
      setIsCancelledModalOpen(true);
      return;
    }

    if (!hasFreeSeats && teamSubscriptionsData?.hasGracePeriodTeamSubscription) {
      setIsGracePeriodModalOpen(true);
      tracker.trackModalShown(ModalShownAction.INVITE_UNAVAILABLE, "is_in_grace_period");
      return;
    }
    setIsInviteModalOpen(true);
  }, [
    isOwner,
    isSeatManager,
    teamSubscriptionsData?.hasNonAutoRenewingSelfServeTeamSubscription,
    teamSubscriptionsData?.hasGracePeriodTeamSubscription,
    hasFreeSeats,
    tracker,
  ]);

  const [selection, setSelection] = useState<Array<string | null | undefined>>([]);

  useEffect(() => {
    setSelection([]);
    setFilter("");
  }, [teamID]);

  const { state } = useLocation();
  useEffect(() => {
    if (state) {
      setHistoryState(state);
    }
  }, [state]);

  useEffect(() => {
    if (isLoading || !historyState) {
      return;
    }

    switch (historyState.Action) {
      case URLStateActions.OpenInviteModal: {
        handleInviteAction();
        break;
      }
    }

    setHistoryState(null);
  }, [handleInviteAction, isLoading, historyState]);

  const isSpaceListingEnabled = teamData?.addons?.teamspaceManagement;
  const canEditSpace = isSpaceListingEnabled && isSeatManager;
  const isCheckboxIndeterminate = useMemo(() => {
    return (
      teamMembersData &&
      Boolean(selection.length) &&
      selection.length <
        (teamMembersData.filter((member) => member.invitationId === undefined)?.length ?? 0)
    );
  }, [selection, teamMembersData]);

  const canRemoveMember = (teamMember: TeamMember): boolean => {
    if (!teamMember) {
      return false;
    }
    return (
      (isOwner || isSeatManager) &&
      teamMember.role !== TeamsUserRoles.Owner &&
      teamMember.userId !== accountData?.id
    );
  };

  const [selectedMemberToAddToSpace, setSelectedMemberToAddToSpace] = useState<TeamMember | null>(
    null
  );
  const [selectedMemberToRemoveFromSpace, setSelectedMemberToRemoveFromSpace] =
    useState<TeamMember | null>(null);

  const isRemoveFromSpaceButtonDisabled = Boolean(
    teamMembersData
      ?.filter((member) => selection.includes(member.userId))
      .every((member) => !member.spaces || member.spaces.length === 0)
  );

  if (isGuest) {
    return <Navigate to={`/teams/${teamID}`} />;
  }

  if (chinaRestricted) {
    return <SpinnerCenter />;
  }

  if (isLoading) {
    return <SpinnerCenter />;
  }

  return (
    <UnderMaintenanceOrChildren>
      <PageContainer>
        <div className={style.titleContainer}>
          <Heading type="h2">Members</Heading>
          <Button
            variant="primary"
            onClick={() => {
              tracker.trackButtonClick({ action: ElementClickAction.INVITE_MEMBERS });
              handleInviteAction();
            }}
          >
            Invite members
          </Button>
        </div>
        {!teamData?.addons?.syncForbidden && (
          <div className={style.teamPromoContainer}>
            <div className={style.teamPromoImage}>
              <MembersVideo />
            </div>

            <div className={style.teamPromoText}>
              <div>Collaborate with your team</div>
              <ul>
                <li>Cut CAD onboarding time to days for your team</li>
                <li>Centralize design sharing in one synced hub</li>
                <li>Curate your team&apos;s part library</li>
              </ul>
            </div>
          </div>
        )}
        <div
          className={classNames(
            style.tableContainer,
            canEditSpace && style.withSelection,
            isSpaceListingEnabled && style.withSpaceList
          )}
        >
          {canEditSpace && (
            <Checkbox
              className={style.tableHeader}
              indeterminate={isCheckboxIndeterminate}
              checked={
                Boolean(selection.length) &&
                selection.length ===
                  teamMembersData.filter((member) => member.invitationId === undefined)?.length
              }
              onChange={(e) => {
                if (isCheckboxIndeterminate) {
                  setSelection([]);
                  tracker.trackButtonClick({
                    action: ElementClickAction.DESELECT_MEMBERS,
                    details: "all",
                    location: ElementClickLocation.MEMBERS_TABLE_HEADER,
                  });
                } else {
                  if (e.target.checked) {
                    setSelection(
                      teamMembersData
                        .filter((member) => member.invitationId === undefined)
                        ?.map((member) => member.userId) ?? []
                    );
                    tracker.trackButtonClick({
                      action: ElementClickAction.SELECT_MEMBERS,
                      details: "all",
                      location: ElementClickLocation.MEMBERS_TABLE_HEADER,
                    });
                  } else {
                    setSelection([]);
                    tracker.trackButtonClick({
                      action: ElementClickAction.DESELECT_MEMBERS,
                      details: "all",
                      location: ElementClickLocation.MEMBERS_TABLE_HEADER,
                    });
                  }
                }
              }}
            />
          )}
          <div className={style.tableHeader}>MEMBER</div>
          <div className={classNames(style.roleColumn, style.tableHeader)}>ROLE</div>
          {isSpaceListingEnabled && (
            <div className={classNames(style.roleColumn, style.tableHeader)}>SPACE</div>
          )}
          <div style={{ width: "40px" }} />
          {canEditSpace && (
            <MemberSelectionFilterRow
              filter={filter}
              setFilter={setFilter}
              selection={selection}
              setSelection={setSelection}
              isRemoveFromSpaceButtonDisabled={isRemoveFromSpaceButtonDisabled}
            />
          )}
          {!canEditSpace && <CTARowDivider className={style.fullRow} variant="none" />}

          {!teamMembersData?.length && (
            <div className={style.emptyState}>
              No members match your search query. Try your search again.
            </div>
          )}

          {teamMembersData.map((teamMember) => (
            <Fragment key={teamMember.userId ?? teamMember.invitationId}>
              <CTARowDivider className={style.fullRow} variant="none" />
              {canEditSpace && (
                <>
                  {teamMember.invitationId && <div />}
                  {!teamMember.invitationId && (
                    <Checkbox
                      checked={selection.includes(teamMember.userId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelection((oldSelection) => {
                            return [...oldSelection, teamMember.userId];
                          });
                          tracker.trackButtonClick({
                            action: ElementClickAction.SELECT_MEMBERS,
                            details: String(selection.length + 1),
                          });
                        } else {
                          setSelection((oldSelection) =>
                            oldSelection.filter((x) => x !== teamMember.userId)
                          );
                          tracker.trackButtonClick({
                            action: ElementClickAction.DESELECT_MEMBERS,
                            details: String(selection.length - 1),
                          });
                        }
                      }}
                    />
                  )}
                </>
              )}
              <div className={style.teamMemberContainer}>
                <div className={style.teamMemberEmailContainer}>
                  {teamMember.name && <div className={style.userEmail}>{teamMember.name}</div>}
                  <div className={style.userEmail}>{teamMember.email}</div>
                </div>
                {Boolean(teamMember.invitationId) && <Capsule>pending</Capsule>}
              </div>
              <div className={classNames(style.userRole, style.roleColumn)}>
                {TeamsUserRolesDisplayNames[teamMember.role]}
              </div>
              {isSpaceListingEnabled && (
                <div className={classNames(style.userRole, style.roleColumn)}>
                  {teamMember.spaces?.map(({ name }) => name ?? defaultTeamspaceName).join(", ")}
                </div>
              )}
              <div>
                {(canEditSpace || canRemoveMember(teamMember)) && (
                  <Menu
                    activator={
                      <Button variant="borderless">
                        <Icon_Kebab />
                      </Button>
                    }
                    onOpen={() => {
                      tracker.trackButtonClick({
                        action: ElementClickAction.OPEN_MEMBER_OPTIONS_MENU,
                      });
                    }}
                    className={style.contextMenu}
                  >
                    {canEditSpace && !teamMember.invitationId && (
                      <Menu.Item
                        onClick={() => {
                          setSelectedMemberToAddToSpace(teamMember);
                          tracker.trackButtonClick({
                            action: ElementClickAction.ADD_TO_SPACE,
                            location: ElementClickLocation.MEMBER_OPTIONS_MENU,
                          });
                        }}
                        closeOnClick
                      >
                        <span>Add to space...</span>
                      </Menu.Item>
                    )}
                    {canEditSpace && !teamMember.invitationId && (
                      <Menu.Item
                        onClick={() => {
                          setSelectedMemberToRemoveFromSpace(teamMember);
                          tracker.trackButtonClick({
                            action: ElementClickAction.REMOVE_FROM_SPACE,
                            location: ElementClickLocation.MEMBER_OPTIONS_MENU,
                          });
                        }}
                        closeOnClick
                        disabled={!teamMember.spaces?.length}
                      >
                        <span>Remove from space...</span>
                      </Menu.Item>
                    )}
                    {canRemoveMember(teamMember) && !teamMember.invitationId && (
                      <Menu.Item
                        onClick={() => {
                          tracker.trackButtonClick({
                            action: ElementClickAction.REMOVE_MEMBER,
                            location: ElementClickLocation.MEMBER_OPTIONS_MENU,
                          });
                          setSelectedMemberToRemove(teamMember);
                        }}
                        closeOnClick
                      >
                        <span className={style.red}>Remove member</span>
                      </Menu.Item>
                    )}
                    {canRemoveMember(teamMember) && teamMember.invitationId && (
                      <Menu.Item
                        closeOnClick
                        onClick={() => {
                          tracker.trackButtonClick({
                            action: ElementClickAction.REMOVE_INVITATION,
                            location: ElementClickLocation.MEMBER_OPTIONS_MENU,
                          });
                          handleRemoval({
                            userID: teamMember.userId,
                            invitationID: teamMember.invitationId,
                          });
                        }}
                      >
                        <span className={style.red}>Remove invitation</span>
                      </Menu.Item>
                    )}
                  </Menu>
                )}
              </div>
            </Fragment>
          ))}
        </div>
      </PageContainer>
      <Modal
        title="Invite members"
        size="small"
        open={isNotTeamOwnerModalOpen}
        onClose={() => {
          setIsNotTeamOwnerModalOpen(false);
        }}
      >
        <div className={style.invitationNotAvailableModal}>
          <span>Only team owners can send invites.</span>
          <span>To add new members to {teamData.name}, contact your team owner.</span>
        </div>
      </Modal>
      <Modal
        title="Invite members"
        size="small"
        open={isCancelledModalOpen}
        onClose={() => {
          setIsCancelledModalOpen(false);
        }}
      >
        <div className={style.invitationNotAvailableModal}>
          <span>Invitation to {teamData.name} is unavailable.</span>
          <div className={style.message}>
            <span>Your subscription has been cancelled.</span>
            <span>Turn on auto-renewal to invite new members.</span>
          </div>
        </div>
      </Modal>
      <Modal
        title="Invite members"
        size="small"
        open={isGracePeriodModalOpen}
        onClose={() => {
          setIsGracePeriodModalOpen(false);
        }}
      >
        <div className={style.invitationNotAvailableModal}>
          <span>Invitation to {teamData.name} is unavailable.</span>
          <div className={style.message}>
            <span>Your subscription is within the grace period.</span>
            <span>Update your card details to invite new members.</span>
          </div>
        </div>
      </Modal>
      <TeamInviteModal
        open={isInviteModalOpen}
        teamID={teamID}
        onClose={() => {
          setIsInviteModalOpen(false);
        }}
      />
      <Modal
        title="Remove member"
        size="small"
        open={Boolean(selectedMemberToRemove)}
        onClose={() => setSelectedMemberToRemove(null)}
      >
        <div className={style.invitationNotAvailableModal}>
          <p>
            {`Removing`} <b>{`${selectedMemberToRemove?.name ?? selectedMemberToRemove?.email}`}</b>
            {` ${selectedMemberToRemove?.name ? `(${selectedMemberToRemove?.email})` : ""} `}
            {`from ${teamData.name} will revoke their access to the team immediately.`}
          </p>
          {teamSubscriptionsData?.seatBasedTeamSubscription &&
            selectedMemberToRemove?.role &&
            !NonSeatConsumingTeamsUserRoles.includes(selectedMemberToRemove?.role) && (
              <span>
                {`Your team will have one additional seat to assign. You can invite new member to that seat anytime.`}
              </span>
            )}
        </div>
        <div className={style.ctaRow}>
          <Button
            variant="red"
            onClick={() => {
              handleRemoval({
                userID: selectedMemberToRemove?.userId,
                invitationID: selectedMemberToRemove?.invitationId,
              });
            }}
          >
            Remove member
          </Button>
        </div>
      </Modal>
      <AddToSpaceModal
        open={Boolean(selectedMemberToAddToSpace)}
        onClose={() => setSelectedMemberToAddToSpace(null)}
        userIDs={[String(selectedMemberToAddToSpace?.userId)]}
      />
      <RemoveFromSpaceModal
        open={Boolean(selectedMemberToRemoveFromSpace)}
        onClose={() => setSelectedMemberToRemoveFromSpace(null)}
        userIDs={[String(selectedMemberToRemoveFromSpace?.userId)]}
      />
    </UnderMaintenanceOrChildren>
  );
};

export default TeamMembersPage;
